interface Error {
  message: string;
}

export interface ApiReturn<T = undefined> {
  success: boolean;
  error?: Error;
  data?: T extends undefined ? never : T;
}

export interface Partner {
  partnerName: string;
  partnerId: string;
}

export interface UnreadNotifications {
  hasUnreadNotifications: boolean;
  notifications: Notification[];
}


enum NotificationType {
  SERVICE = "SERVICE",
  OFFER = "OFFER",
}

export interface Notification {
  createdAt: string;
  dpcId: string;
  message: string;
  read: boolean;
  reference: string;
  title: string;
  type: NotificationType;
}

export enum Condition {
  Poor = "Poor",
  Used = "Used",
  LikeNew = "Like new",
  BrandNew = "BrandNew",
}

export enum ServiceState {
  ServiceRequested = "serviceRequested",
  ServiceInReview = "serviceInReview",
  ServiceAwaitingResponse = "serviceAwaitingResponse",
  ServiceAwaitingProduct = "serviceAwaitingProduct",
  ServiceProductTransit = "serviceProductTransit",
  InService = "inService",
  ServiceReady = "serviceReady",
  ServiceAwaitingAcceptance = "serviceAwaitingAcceptance",
  ServiceCancelledUser = "serviceCancelledUser",
  ServiceCancelledPartner = "serviceCancelledPartner",
  ServiceSolutionRejected = "serviceSolutionRejected",
}

export enum ServiceType {
  Repair = "repair",
  Maintenance = "maintenance",
  ReplacingParts = "replacingParts",
  SoftwareUpdate = "softwareUpdate",
  Cleaning = "cleaning",
}

export interface Dpc {
  category: string;
  condition: Condition;
  dpcId: string;
  location: string;
  productName: string;
  serviceState: ServiceState;
  warrantyEndDate: string;
  orderNumber: string;
  sku: string;
}

export interface Service {
  dateUpdated: string;
  serviceId: string;
  serviceNumber: string;
  serviceRequested: string;
  serviceType: ServiceType;
  state: ServiceState;
}

export enum File {
  Image = "IMG",
  Pdf = "PDF",
}

interface ServiceInformationDoc {
  fileType: File;
  serviceDocumentId: string;
  serviceDocumentUrl: string;
}

export interface ServiceInformation {
  docs: ServiceInformationDoc[] | null;
  message: string;
  state: ServiceState;
  updated: string;
  url: string | null;
  urlText: string | null;
}

export interface ServiceExtended {
  currentOwner: string;
  dpcId: string;
  insuranceInformation: string | null;
  partnerId: string;
  serviceDescription: string;
  serviceDocs: ServiceInformationDoc[];
  serviceInformation: ServiceInformation[];
  serviceNumber: string;
  servicePartner: string | null;
  serviceRequested: string;
  serviceType: ServiceType;
  state: ServiceState;
  warrantyInformation: string | null;
}

export interface Campaign {
  id: string;
  name: string;
  status: CampaignStatus;
}

export interface CampaignProduct {
  label: string;
  value: string;
}

export interface CampaignBrand extends CampaignProduct {}

interface AnalyticsAudienceReachedItem {
  amount: number;
  percentage: number;
}
interface AnalyticsAudienceReached {
  read: AnalyticsAudienceReachedItem;
  opened: AnalyticsAudienceReachedItem;
  clicked: AnalyticsAudienceReachedItem;
}

export interface AnalyticsCampaign {
  campaignId: string;
  title: string;
  amountReached: number;
  audienceReached: AnalyticsAudienceReached;
}

export interface ServiceStatusses {}

export enum KanbanBoardStatus {
  Draft = "draft",
  Live = "live",
  Archived = "archived",
}

export enum KanbanPriority {
  High = "high",
  Medium = "medium",
  Low = "low",
}

export enum CampaignStatus {
  Live = "live",
  Archived = "archived",
}

export interface AnalyticsArgs {
  campaignIds: string[];
  startDate: string;
  endDate: string;
  ageBracket: [number, number];
  brands: string[];
  countries: string[];
  gender: string;
  productIds: string[];
}

export interface Product {
  sku: string | null;
  brand: {
    name: string;
    [key: string]: any; // optional if more fields may exist
  };
  model: string;
  descrtiption: string;
  category: {
    mainCategory: string;
    subCategory: string;
  };
  edition: string | null;
  price: {
    currency: string;
    value: string;
  };
  [key: string]: any;
}

export type ProductPayload = {
  brand:string,
  model: string;
  description: string;
  sku: string;
  edition?: string;
  category: {
    mainCategory: string;
    subCategory?: string;
  };
  price: {
    currency: string;
    value: string;
  };
};
